import React, { useEffect, useRef, useState } from 'react'

// Full-screen welcome carousel for logged-out MOBILE visitors — a web port of
// the app's (auth)/welcome.tsx. Three swipeable slides (EV data-flow hero,
// fanned phone mockups, self-drawing profit chart) with crossfading headlines,
// dot pager and fixed auth CTAs. Desktop keeps the AuthModal wall.

const LOGO = 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/BC_LOGO_200x200.png'
const APP_IMG = 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/APP_IMAGES'
const OPP_CARD = `${APP_IMG}/EV_OPP_BET365.png`

const SLIDES = [
  {
    id: 'intro',
    accent: '#3b82f6',
    heading: 'Beat the bookies\nat their own game',
    sub: 'Start making a regular income off the bookmakers with a mathematical approach to betting.'
  },
  {
    id: 'tools',
    accent: '#34d399',
    heading: 'Every profitable\nstrategy, in one app',
    sub: '+EV, arbitrage, bonus conversion and more — every edge the pros use, scanned for you in real time.'
  },
  {
    id: 'tracker',
    accent: '#60a5fa',
    heading: 'Watch your\nprofit grow',
    sub: 'Profit charts, ROI and a calendar that tracks your real results against expected value.'
  }
]

const FLOW_BOOKS = [
  { name: 'Sportsbet', logo: `${APP_IMG}/sportsbetcircle.png`, odds: '1.87' },
  { name: 'Betr', logo: `${APP_IMG}/betrcircle.png`, odds: '1.90' },
  { name: 'Dabble', logo: `${APP_IMG}/dabblecircle.png`, odds: '1.85' },
  { name: 'Bet365', logo: `${APP_IMG}/bet365circle.png`, odds: '2.02', best: true },
  { name: 'TAB', logo: `${APP_IMG}/tabcircle.png`, odds: '1.91' },
  { name: 'Pinnacle', logo: `${APP_IMG}/pinnaclecircle.png`, odds: '1.89' },
  { name: 'theScore', logo: `${APP_IMG}/thescorebetcicle.png`, odds: '1.88' }
]
const FAIR_ODDS = '1.892'

// ── Slide 1: bookie dome → fair-odds hub → real opportunity card ────────────

const BookNode = ({ logo, odds, best, lift }) => {
  const ring = best ? '#34d399' : '#3b82f6'
  const SIZE = 34
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', transform: `translateY(${-lift}px)` }}>
      <img
        src={logo}
        alt=""
        style={{
          width: SIZE, height: SIZE,
          borderRadius: '50%',
          border: `2px solid ${ring}`,
          boxShadow: `0 0 ${best ? 11 : 8}px 0 ${ring}${best ? '80' : '52'}`,
          objectFit: 'cover',
          display: 'block'
        }}
      />
      <span style={{ marginTop: '6px', fontSize: '10px', fontWeight: '700', fontVariantNumeric: 'tabular-nums', color: best ? '#34d399' : '#cbd5e1' }}>
        {odds}
      </span>
    </div>
  )
}

const HeroOppCard = () => {
  const W = 300
  const H = 96
  const hubX = W / 2
  const hubY = H - 12
  const nodeY = 10
  const N = FLOW_BOOKS.length
  const edge = 26
  const nodeXs = FLOW_BOOKS.map((_, i) => edge + (i * (W - 2 * edge)) / (N - 1))
  const nodeYs = FLOW_BOOKS.map((_, i) => {
    const t = (i / (N - 1)) * 2 - 1
    return nodeY + (t * t) * 30
  })
  const paths = nodeXs.map((x, i) => `M ${x} ${nodeYs[i]} C ${x} ${H / 2}, ${hubX} ${H / 2}, ${hubX} ${hubY}`)

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '0 16px' }}>
      <div style={{ width: '100%', maxWidth: '250px' }}>
        {/* Bookie nodes — arced into a dome */}
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {FLOW_BOOKS.map((b, i) => {
            const t = (i / (FLOW_BOOKS.length - 1)) * 2 - 1
            const lift = Math.round((1 - t * t) * 30)
            return <BookNode key={b.name} {...b} lift={lift} />
          })}
        </div>

        {/* Flow lines converging to the hub */}
        <div style={{ height: `${H}px`, marginTop: '-34px', pointerEvents: 'none' }}>
          <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
            <defs>
              <linearGradient id="bcWelcomeLine" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#3b82f6" stopOpacity="0.25" />
                <stop offset="1" stopColor="#10b981" stopOpacity="0.5" />
              </linearGradient>
            </defs>
            {paths.map((d, i) => (
              <path key={`b${i}`} d={d} stroke="url(#bcWelcomeLine)" strokeWidth="1.5" fill="none" />
            ))}
            {paths.map((d, i) => (
              <path
                key={`p${i}`}
                d={d}
                stroke="#34d399"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="10 30"
                style={{ animation: 'bcWelcomeFlow 1.5s linear infinite' }}
              />
            ))}
          </svg>
        </div>

        {/* Fair Odds / Win Prob hub */}
        <div style={{ marginTop: '-48px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', overflow: 'hidden', borderRadius: '16px', border: '1px solid rgba(16,185,129,0.45)', background: '#0b1322' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 16px' }}>
              <span style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b' }}>Fair Odds</span>
              <span style={{ marginTop: '2px', fontSize: '18px', fontWeight: '800', color: '#34d399', fontVariantNumeric: 'tabular-nums' }}>{FAIR_ODDS}</span>
            </div>
            <div style={{ width: '1px', background: '#1e2a40' }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 16px' }}>
              <span style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b' }}>Win Prob</span>
              <span style={{ marginTop: '2px', fontSize: '18px', fontWeight: '800', color: '#60a5fa', fontVariantNumeric: 'tabular-nums' }}>52.9%</span>
            </div>
          </div>
        </div>

        {/* Connector down to the card */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '2px', height: '8px', background: 'rgba(16,185,129,0.5)' }} />
        </div>

        {/* Real opportunity card — gradient border + glow */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{
            width: '74%',
            maxWidth: '220px',
            borderRadius: '18px',
            padding: '1.5px',
            background: 'linear-gradient(135deg, #34d399 0%, #3b82f6 50%, #34d399 100%)',
            boxShadow: '0 0 18px rgba(16,185,129,0.25)'
          }}>
            <div style={{ overflow: 'hidden', borderRadius: '16px', background: '#010714' }}>
              <img src={OPP_CARD} alt="Live +EV opportunity" style={{ width: '100%', aspectRatio: '828 / 552', objectFit: 'cover', display: 'block' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Slide 2: three fanned phone mockups ─────────────────────────────────────

const FannedPhone = ({ src, width, accent, style }) => {
  const ASPECT = 1179 / 2556
  const height = width / ASPECT
  return (
    <div style={{ width: `${width}px`, height: `${height}px`, position: 'relative', ...style }}>
      <div style={{
        position: 'absolute',
        top: `${height * 0.11}px`,
        bottom: `${height * 0.04}px`,
        left: `${width * 0.06}px`,
        right: `${width * 0.06}px`,
        borderRadius: `${width * 0.16}px`,
        boxShadow: `0 0 26px 0 ${accent}`,
        pointerEvents: 'none'
      }} />
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative', display: 'block' }} />
    </div>
  )
}

const ToolsMockup = () => (
  <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
    {/* Fixed-size fan of three phones. Kept at its natural size (no clipping)
        so all three always show; the parent flex area centres it. */}
    <div style={{ position: 'relative', width: '280px', height: '300px' }}>
      <FannedPhone
        src={`${APP_IMG}/BONUS_APP-portrait.png`}
        width={132}
        accent="#a78bfa"
        style={{ position: 'absolute', left: '50%', top: '36px', transform: 'translateX(-50%) translateX(-72px) rotate(-11deg)' }}
      />
      <FannedPhone
        src={`${APP_IMG}/ARB_APP-portrait.png`}
        width={132}
        accent="#fbbf24"
        style={{ position: 'absolute', left: '50%', top: '36px', transform: 'translateX(-50%) translateX(72px) rotate(11deg)' }}
      />
      <FannedPhone
        src={`${APP_IMG}/EV_APP-portrait.png`}
        width={152}
        accent="#34d399"
        style={{ position: 'absolute', left: '50%', top: '4px', transform: 'translateX(-50%)' }}
      />
    </div>
  </div>
)

// ── Slide 3: self-drawing profit chart ──────────────────────────────────────

const TRACKER_ACCENT = '#34d399'
const EXPECTED_ACCENT = '#fbbf24'
const PROFIT_POINTS = [0.04, 0.1, 0.08, 0.2, 0.26, 0.22, 0.36, 0.46, 0.43, 0.58, 0.7, 0.82, 1.0]
const EXPECTED_POINTS = [0.03, 0.08, 0.13, 0.19, 0.25, 0.31, 0.38, 0.45, 0.53, 0.62, 0.71, 0.8, 0.9]
const PROFIT_TARGET = 2480
const LINE_LEN = 520

function buildSmoothPath(pts) {
  if (pts.length < 2) return ''
  let d = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] || p2
    d += ` C ${p1.x + (p2.x - p0.x) / 6} ${p1.y + (p2.y - p0.y) / 6}, ${p2.x - (p3.x - p1.x) / 6} ${p2.y - (p3.y - p1.y) / 6}, ${p2.x} ${p2.y}`
  }
  return d
}

const TrackerMockup = () => {
  const W = 300
  const H = 150
  const PAD_X = 14
  const PAD_TOP = 18
  const PAD_BOT = 18
  const plotW = W - PAD_X * 2
  const plotH = H - PAD_TOP - PAD_BOT
  const toXY = (v, i, n) => ({ x: PAD_X + (i / (n - 1)) * plotW, y: PAD_TOP + (1 - v) * plotH })
  const pts = PROFIT_POINTS.map((v, i) => toXY(v, i, PROFIT_POINTS.length))
  const expPts = EXPECTED_POINTS.map((v, i) => toXY(v, i, EXPECTED_POINTS.length))
  const linePath = buildSmoothPath(pts)
  const expPath = buildSmoothPath(expPts)

  const [value, setValue] = useState(0)

  useEffect(() => {
    let raf
    let start = performance.now()
    const DRAW = 2200, HOLD = 1400, GAP = 250
    const tick = (now) => {
      const t = (now - start) % (DRAW + HOLD + GAP)
      setValue(t < DRAW ? t / DRAW : t < DRAW + HOLD ? 1 : 0)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // Sample the poly-line at parameter value for the tip dot + partial area.
  const span = (pts.length - 1) * Math.min(Math.max(value, 0), 1)
  const i = Math.min(Math.floor(span), pts.length - 2)
  const f = span - i
  const dot = {
    x: pts[i].x + (pts[i + 1].x - pts[i].x) * f,
    y: pts[i].y + (pts[i + 1].y - pts[i].y) * f,
    o: Math.min(value * 8, 1)
  }
  const base = H - PAD_BOT
  const reached = pts.slice(0, Math.floor(span) + 1)
  const areaD = value <= 0 ? '' :
    `M ${pts[0].x} ${base} ` + [...reached, dot].map(q => `L ${q.x} ${q.y}`).join(' ') + ` L ${dot.x} ${base} Z`
  const profit = Math.round(value * PROFIT_TARGET)
  const expOpacity = value < 0.15 ? (value / 0.15) * 0.7 : 0.7

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: `${W}px`, maxWidth: '100%', borderRadius: '24px', border: '1px solid rgba(52,211,153,0.25)', background: '#0b1322', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '16px 20px 0' }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b' }}>Net Profit</div>
            <div style={{ marginTop: '2px', fontSize: '26px', fontWeight: '800', color: '#34d399', fontVariantNumeric: 'tabular-nums' }}>
              +${profit.toLocaleString()}
            </div>
          </div>
          <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '999px', background: '#10241c', padding: '4px 8px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
            </svg>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#34d399' }}>+18.4% ROI</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '3px', borderRadius: '2px', background: TRACKER_ACCENT }} />
            <span style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8' }}>Actual</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', borderTop: `2px dashed ${EXPECTED_ACCENT}` }} />
            <span style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8' }}>Expected</span>
          </div>
        </div>

        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="bcWelcomeArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={TRACKER_ACCENT} stopOpacity="0.32" />
              <stop offset="1" stopColor={TRACKER_ACCENT} stopOpacity="0" />
            </linearGradient>
          </defs>
          {areaD ? <path d={areaD} fill="url(#bcWelcomeArea)" /> : null}
          <path d={expPath} stroke={EXPECTED_ACCENT} strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="5 6" opacity={expOpacity} />
          <path d={linePath} stroke={TRACKER_ACCENT} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={LINE_LEN} strokeDashoffset={LINE_LEN * (1 - value)} />
          <circle cx={dot.x} cy={dot.y} r="12" fill={TRACKER_ACCENT} opacity={dot.o * 0.18} />
          <circle cx={dot.x} cy={dot.y} r="6.5" fill={TRACKER_ACCENT} opacity={dot.o} />
          <circle cx={dot.x} cy={dot.y} r="2.8" fill="#0b1322" opacity={dot.o} />
        </svg>
      </div>
    </div>
  )
}

const SlideVisual = ({ slide }) => {
  if (slide.id === 'intro') return <HeroOppCard />
  if (slide.id === 'tools') return <ToolsMockup />
  if (slide.id === 'tracker') return <TrackerMockup />
  return null
}

// ── The screen ───────────────────────────────────────────────────────────────

export default function WelcomeMobile({ onAuth }) {
  const scrollRef = useRef(null)
  const [progress, setProgress] = useState(0)

  // Mark the carousel as seen — next logged-out visit auto-opens sign-in.
  useEffect(() => {
    localStorage.setItem('bc-welcome-seen', 'true')
  }, [])
  const index = Math.min(SLIDES.length - 1, Math.max(0, Math.round(progress)))

  const handleScroll = (e) => {
    const el = e.currentTarget
    if (el.clientWidth > 0) setProgress(el.scrollLeft / el.clientWidth)
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#010714',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 500,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      overflow: 'hidden'
    }}>
      <style>{`
        @keyframes bcWelcomeFlow {
          to { stroke-dashoffset: -40; }
        }
        .bc-welcome-scroll {
          scrollbar-width: none;
        }
        .bc-welcome-scroll::-webkit-scrollbar { display: none; }
        @media (prefers-reduced-motion: reduce) {
          .bc-welcome-scroll * { animation: none !important; }
        }
      `}</style>

      {/* Brand */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 'max(28px, env(safe-area-inset-top))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={LOGO} alt="" style={{ width: '28px', height: '28px', borderRadius: '8px' }} />
          <span style={{ fontSize: '17px', fontWeight: '700', letterSpacing: '-0.01em', color: '#ffffff' }}>BookieCharity</span>
        </div>
      </div>

      {/* Swipe region */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', marginTop: '18px' }}>
        <div
          ref={scrollRef}
          className="bc-welcome-scroll"
          onScroll={handleScroll}
          style={{
            height: '100%',
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {SLIDES.map((sl) => (
            <div key={sl.id} style={{
              minWidth: '100%',
              height: '100%',
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box'
            }}>
              {/* Top guard so no visual can tap into the brand header. */}
              <div style={{ height: '16px', flexShrink: 0 }} />
              {/* Visual sits in the space above the reserved text band and is
                  vertically centred. Not clipped, so lifted hero nodes show. */}
              <div style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 0'
              }}>
                <SlideVisual slide={sl} />
              </div>
              {/* Reserved space the headline overlay sits in (kept as an overlay
                  so the scroll-synced crossfade still works). */}
              <div style={{ height: '168px', flexShrink: 0 }} />
            </div>
          ))}
        </div>

        {/* Headline + sub — crossfade synced to scroll */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: '32px', minHeight: '112px', pointerEvents: 'none' }}>
          {SLIDES.map((sl, i) => {
            const opacity = Math.max(0, 1 - Math.abs(progress - i))
            if (opacity <= 0.01) return null
            return (
              <div key={sl.id} style={{ position: 'absolute', left: '28px', right: '28px', bottom: 0, opacity, textAlign: 'center' }}>
                <div style={{
                  fontSize: '23px',
                  fontWeight: '800',
                  lineHeight: '28px',
                  letterSpacing: '-0.5px',
                  color: '#ffffff',
                  whiteSpace: 'pre-line',
                  textShadow: `0 0 16px ${sl.accent}55`
                }}>
                  {sl.heading}
                </div>
                <div style={{ marginTop: '9px', fontSize: '12.5px', lineHeight: '18px', color: '#94a3b8' }}>
                  {sl.sub}
                </div>
              </div>
            )
          })}
        </div>

        {/* Dot pager */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: '8px', display: 'flex', justifyContent: 'center', gap: '6px', pointerEvents: 'none' }}>
          {SLIDES.map((sl, i) => (
            <span key={sl.id} style={{
              height: '6px',
              width: i === index ? '22px' : '6px',
              borderRadius: '3px',
              background: i === index ? sl.accent : 'rgba(148,163,184,0.3)',
              transition: 'width 0.25s ease, background 0.25s ease'
            }} />
          ))}
        </div>
      </div>

      {/* Auth CTAs */}
      <div style={{ padding: '8px 24px calc(16px + env(safe-area-inset-bottom))' }}>
        <button
          onClick={() => onAuth('signup')}
          style={{
            width: '100%',
            padding: '15px 0',
            borderRadius: '12px',
            border: 'none',
            background: '#2563eb',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '0 2px 12px rgba(37, 99, 235, 0.3)'
          }}
        >
          Get Started
        </button>
        <button
          onClick={() => onAuth('signin')}
          style={{
            width: '100%',
            marginTop: '12px',
            padding: '8px 0',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '14px',
            color: '#64748b'
          }}
        >
          Already have an account? <span style={{ color: '#60a5fa', fontWeight: '600' }}>Log in</span>
        </button>
      </div>
    </div>
  )
}
